import type { TreeNode } from '@capsio/contracts';
import type { ReactNode } from 'react';
import { HomeNumbers } from './HomeNumbers.js';
import { PositionHeatmap } from '../prototype/PositionHeatmap.js';
import { StationOverview } from '../prototype/StationOverview.js';
import { RightRail } from '../prototype/RightRail.js';
import { childrenOf, ROOT_ID, stationAtcGroups, typeOf, type TreeIndex } from './node-tree-path.js';

/**
 * 组织节点的渲染区：一行元信息 + 这一站的 ATC 卡片。
 *
 * 卡片和侧栏「ATC」页列的是同一批，这是刻意的两种密度——侧栏那一列窄、是选择器，
 * 一边看详情一边换着挑；这儿宽、是浏览区，名字和编码都摊开看得清。
 *
 * 卡片是**这一站往下整支**的 ATC，按「挂在谁身上」分段：选中项目开发部，
 * 它下面各个岗位的标准都摊在这儿。**段头只给下级那几段**——这一站自己的那批排在最前面，
 * 不用标；标了反而多一行「本级」这种只有系统才会这么说话的词。
 *
 * 元信息只留**别处看不到、而且不为零**的那几样：挂了几个人、停用没有。
 * 类型、编码、下级数、上级路径都撤了——
 * 类型和层级在侧栏靠排版就看得出，上级路径是面包屑的活儿，下级数树里数得出来，
 * 编码平时没人读。一行里摆着五个没人看的标签，比空着更吵。
 *
 * 也不放大标题：名字已经在面包屑最后一段了。
 */
export function UnitDetail({
  index,
  node,
  canCreate,
  onSelect,
}: {
  index: TreeIndex;
  /** null 表示企业本身——它不是一条节点记录。 */
  node: TreeNode | null;
  /** 有没有建 ATC 的权限。这儿只决定空态怎么说——建的入口在页头的动作条上。 */
  canCreate: boolean;
  onSelect: (id: string) => void;
}): ReactNode {
  const id = node?.id ?? ROOT_ID;
  const groups = stationAtcGroups(index, id);
  const total = groups.reduce((sum, group) => sum + group.atcs.length, 0);

  /**
   * 这一站下面的岗位。热力图一行对应一个岗位（原型 1213 行 `ps.map(posRoomRow)`）。
   *
   * 企业那一站取全部岗位、部门那一站取自己的——**与原型同构**：
   * 原型的企业级与部门级用的是同一个 `posRoomRow`，只是喂进去的 `ps` 不同。
   */
  const positionsHere =
    node === null
      ? childrenOf(index, ROOT_ID).flatMap((d) => childrenOf(index, d.id))
          .filter((n) => typeOf(index, n)?.code === 'POSITION')
      : childrenOf(index, node.id).filter((n) => typeOf(index, n)?.code === 'POSITION');

  const members = node?.memberCount ?? 0;
  const archivedUnit = node !== null && !node.enabled;
  // 两样都没有就整行不渲染，别留一条空的横线。
  const hasMeta = members > 0 || archivedUnit;

  return (
    <article className="unit">
      {/*
        首屏五数只在**企业**这一站显示（ADR-0047：企业级与部门级首屏）。
        部门级另有自己的口径，本轮未做——不在这里凑合着复用一份，
        那会让部门看到全公司的数字而不自知。
      */}
      {node === null && <HomeNumbers />}

      {/*
        ## 两种站，两套块——**照原型分**（C-160 A6 并排对照后校正）

        | 站 | 原型出处 | 装什么 |
        |---|---|---|
        | 企业站 | `homeHTML`（1002–1060 行，`.entgrid` 两栏） | 岗位 ATC 热力图 + **右栏**（运营秘书 / 运行日志） |
        | 部门 / 岗位站 | `depHTML`（1194–1224 行） | 指标条 → 任务卡平面图 → 岗位热力图 → 时间热力图 → 视角，**无右栏** |

        上一版**两种站都挂了右栏**，而原型的右栏只在企业总览里
        （它在 `homeHTML` 的 `.entgrid` 第二列，`depHTML` 里根本没有）。
        并排对照图一眼看出来——逐屏截图看不出来，因为单看每一屏都「有内容」。

        块内样式全部来自 `prototype-v022.css`（`.pv22` 作用域）；
        文档级 reset 已在生成时丢弃，故包住的应用元素不会被清样式。
      */}
      {node === null && (
        <div className="pv22 pv22-main">
          <PositionHeatmap index={index} positions={positionsHere} onPickAtc={onSelect} />
          <RightRail />
        </div>
      )}

      {node !== null && <StationOverview index={index} node={node} onSelect={onSelect} />}

      {hasMeta && (
        <div className="meta-row">
          {members > 0 && (
            <dl className="meta">
              <dt>人</dt>
              <dd className="mono">{members}</dd>
            </dl>
          )}
          {archivedUnit && (
            <dl className="meta">
              <dt>状态</dt>
              <dd>
                <span className="chip wait">已停用</span>
              </dd>
            </dl>
          )}
        </div>
      )}

      {/*
        ## 旧的「卡片列表」已撤

        这里原来还按「挂在谁身上」分组列一遍卡片。**四层树 + 任务卡平面图之后它是第三份**：
        树上有卡、平面图上有卡、这里又有一遍，同一批东西在一屏里出现三次。

        平面图那一份信息更全（状态、成熟度、触发方式、均耗、空卡位），故留平面图，撤这一份。
      */}

      {/*
        这一片不放创建入口，它是路径条末尾那个「+」——挨着当前这一段，
        所以不用解释建到哪儿去。

        并列在卡片里的那张虚线卡（试过）会随卡片数量到处跑，每次要建都得先找它。
        空着的时候只说话，不再给第二个入口。
      */}
      {total === 0 && (
        <p className="sub-hint">
          {canCreate
            ? '这一站还没有 ATC——按路径末尾那个「+」建第一份。'
            : '等管理员在这儿创建 ATC。'}
        </p>
      )}
    </article>
  );
}
